import React, { useEffect, useState } from "react";

/**
 * Continuous typewriter slogan. The first half "Publish Your " stays static
 * and the rotating phrases cycle: "Research Ideas!" → "Dream Concepts!" →
 * back. Each phrase types in, holds, deletes, then the next one types in.
 * Respects prefers-reduced-motion.
 */
export default function AnimatedSlogan({
  prefix = "Publish Your ",
  phrases = ["Research Ideas! ", "Dream Concepts! "],
  className = "",
  typeMs = 70,
  deleteMs = 40,
  holdMs = 1800,
}) {
  const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [typed, setTyped] = useState(reduced ? phrases[0] : "");
  const [phase, setPhase] = useState("typing"); // typing | holding | deleting

  useEffect(() => {
    if (reduced) return;
    const target = phrases[phraseIdx];
    let t;
    if (phase === "typing") {
      if (typed.length < target.length) {
        t = setTimeout(() => setTyped(target.slice(0, typed.length + 1)), typeMs);
      } else {
        t = setTimeout(() => setPhase("holding"), 0);
      }
    } else if (phase === "holding") {
      t = setTimeout(() => setPhase("deleting"), holdMs);
    } else {
      if (typed.length > 0) {
        t = setTimeout(() => setTyped(target.slice(0, typed.length - 1)), deleteMs);
      } else {
        setPhraseIdx((i) => (i + 1) % phrases.length);
        setPhase("typing");
      }
    }
    return () => clearTimeout(t);
  }, [typed, phase, phraseIdx, phrases, typeMs, deleteMs, holdMs, reduced]);

  // Reserve vertical room for the longest phrase so the page never shifts when
  // text wraps to a new line during typing. The invisible ghost sits in normal
  // flow; the visible animated H1 is absolutely positioned over it.
  const longest = phrases.reduce((a, b) => (b.length > a.length ? b : a), "");
  return (
    <div className="relative w-full" data-testid="hero-animated-slogan-wrap">
      <h1
        aria-hidden="true"
        className={`font-serif text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.05] invisible select-none ${className}`}
      >
        <span>{prefix}</span>
        <span className="italic">{longest}</span>
      </h1>
      <h1
        className={`font-serif text-4xl sm:text-5xl lg:text-7xl text-kb-ink tracking-tight leading-[1.05] absolute inset-0 ${className}`}
        data-testid="hero-animated-slogan"
        aria-label={`${prefix}${phrases[phraseIdx]}`}
      >
        <span>{prefix}</span>
        <span className="italic kb-slogan-emphasis-gradient">{typed}</span>
        <span className="kb-slogan-caret" aria-hidden="true">|</span>
      </h1>
    </div>
  );
}
